import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { LocationOrder } from './location.store';
import {
  RoleResource,
  UserResource,
  LocationCredResource,
  CustomerResource,
  UserInfoResource,
} from 'src/models/user.model';
import { computed, inject } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { GlobalPermissions, LocalPermissions } from 'src/models/global-enum';
import { TaskItemType } from 'src/models/task-item.model';
import { FaviconService } from 'src/services/favicon.service';
import { UserService } from 'src/services/user.service';

export type GeneralState = {
  isLoaded: boolean;
  sidebarExpanded: boolean;
  selectedLocationId?: number | null;
  allowedMaxFileUploadMb: string;
  assignedLocations: LocationOrder[];
  isTaskBoardUser: boolean;
  userInfo: UserInfoResource | null;
  isLoggedIn?: boolean;
  globalCreds: GlobalPermissions[];
  locationCreds: Map<number, number[]>;
  loadedNotifier?: Observable<GeneralState> | null;
  profiles: RoleResource[];
  taskItemTypes: TaskItemType[];
  logoUrl: string | null;
  iconUrl: string | null;
  ssoLogins: string[] | null;
  loading: boolean;
  domain: string;
};

const defaultGeneralState: GeneralState = {
  sidebarExpanded: true,
  selectedLocationId: null,
  allowedMaxFileUploadMb: '',
  assignedLocations: [],
  isTaskBoardUser: false,
  userInfo: null,
  isLoggedIn: false,
  globalCreds: [] as number[],
  locationCreds: new Map<number, number[]>(),
  loadedNotifier: null,
  profiles: [] as RoleResource[],
  taskItemTypes: [] as TaskItemType[],
  logoUrl: null,
  iconUrl: null,
  ssoLogins: null,
  // The difference between isLoaded and isLoggedIn is that
  // isLoaded is set to true when the user's data is loaded and the store is ready,
  // loading can be set on any page to show the loading spinner.
  isLoaded: false,
  loading: false,
  domain: '',
};

export const GeneralStore = signalStore(
  { providedIn: 'root' },
  withState<GeneralState>(defaultGeneralState),
  withComputed(({ assignedLocations, locationCreds, globalCreds }) => {
    const isLocalAdminOnAtLeastOneLocation = computed(() => {
      return assignedLocations().some(location => {
        const creds = locationCreds().get(location.id);
        return creds?.includes(LocalPermissions.LOCAL_ADMIN);
      });
    });
    return {
      isLocalAdminOnAtLeastOneLocation,
      isLocalViewerOnAtLeastOneLocationAndNoGlobalPermissions: computed(
        () =>
          isLocalAdminOnAtLeastOneLocation() &&
          !globalCreds().includes(GlobalPermissions.GLOBAL_ADMIN) &&
          !globalCreds().includes(GlobalPermissions.GLOBAL_VIEWER),
      ),
      isGlobalAdmin: computed(() => globalCreds().includes(GlobalPermissions.GLOBAL_ADMIN)),
      isGlobalViewer: computed(() => globalCreds().includes(GlobalPermissions.GLOBAL_VIEWER)),
      isReportAdmin: computed(() => globalCreds().includes(GlobalPermissions.REPORT_ADMIN)),
      isReportViewer: computed(() => globalCreds().includes(GlobalPermissions.REPORT_VIEWER)),
    };
  }),
  withMethods(
    (store, userService = inject(UserService), faviconService = inject(FaviconService)) => ({
      setSidebarExpanded(isExpanded: boolean): void {
        patchState(store, { sidebarExpanded: isExpanded });
      },
      reset() {
        const newState: GeneralState = {
          ...defaultGeneralState,
          logoUrl: store.logoUrl(),
          iconUrl: store.iconUrl(),
          ssoLogins: store.ssoLogins(),
        };
        patchState(store, newState);
      },
      setCurrentUser(user: UserResource) {
        const localCreds: Map<number, number[]> = new Map<number, number[]>();
        user.locationCreds.forEach((cred: LocationCredResource) => {
          if (cred.allowed) {
            let creds = localCreds.get(cred.locationID);
            if (creds) {
              creds.push(cred.credentialID);
            } else {
              creds = [cred.credentialID];
              localCreds.set(cred.locationID, creds);
            }
          }
        });

        const permissionsArray: GlobalPermissions[] = [];
        if (user.globalCreds?.global_admin) {
          permissionsArray.push(GlobalPermissions.GLOBAL_ADMIN);
        }
        if (user.globalCreds?.global_viewer) {
          permissionsArray.push(GlobalPermissions.GLOBAL_VIEWER);
        }
        if (user.globalCreds?.report_admin) {
          permissionsArray.push(GlobalPermissions.REPORT_ADMIN);
        }
        if (user.globalCreds?.report_viewer) {
          permissionsArray.push(GlobalPermissions.REPORT_VIEWER);
        }

        const newState: GeneralState = {
          ...defaultGeneralState,
          domain: store.domain(),
          allowedMaxFileUploadMb: user.allowedMaxFileUploadMb ?? '',
          assignedLocations: user.assignedLocations ?? [],
          isTaskBoardUser: user.isTaskBoardUser ?? false,
          userInfo: user.userInfo ?? null,
          isLoaded: true,
          locationCreds: localCreds,
          globalCreds: permissionsArray,
          profiles: user.profiles,
          taskItemTypes: user.taskItemTypes,
          logoUrl: store.logoUrl(),
          iconUrl: store.iconUrl(),
          ssoLogins: store.ssoLogins(),
          loading: false,
        };

        patchState(store, newState);
        return newState;
      },
      getCurrentUser(force = false) {
        if (force) {
          this.currentUserRawFetch();
        } else if (!store.loadedNotifier?.()) {
          const loadedObservable: Observable<GeneralState> = new Observable(subscriber => {
            this.currentUserRawFetch(subscriber);
          });
          patchState(store, { loadedNotifier: loadedObservable });
        }
      },
      currentUserRawFetch(subscriber?: Subscriber<GeneralState>) {
        userService.getCurrentUser().subscribe(res => {
          const newState: GeneralState = this.setCurrentUser(res);
          if (subscriber) {
            subscriber.next(newState);
            subscriber.complete();
          }
          patchState(store, { loadedNotifier: null });
          if (store.iconUrl()) faviconService.setFavicon(store.iconUrl()!);
        });
      },
      setDomain(domain: string) {
        patchState(store, { domain: domain });
      },
      setCustomerInfo(customerInfo: CustomerResource) {
        const logoUrl: string = customerInfo.logoUrl || './assets/terlumina-logo.svg';
        patchState(store, {
          logoUrl: logoUrl,
          iconUrl: customerInfo.iconUrl,
          // TODO: BE is typing this wrong
          ssoLogins: customerInfo.ssologins as unknown as string[],
        });
      },
      setLoading(loading: boolean) {
        patchState(store, { loading });
      },
    }),
  ),
);
